import { Component, WritableSignal, computed, inject, input, output, viewChild } from '@angular/core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  RowClassParams,
  RowClassRules,
  ValueFormatterParams
} from 'ag-grid-community';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { map, of, pipe, switchMap } from 'rxjs';
import { Action, SimpleGridComponent } from '@app/shared/components/simple-grid/simple-grid.component';
import { DeleteEventDataModel } from '@app/shared/components/simple-grid/simple-grid.model';
import { ReservationValueDetailsModel, ReservationValueListModel } from './reservation-values.model';
import { ReservationValuesUpsertDialogComponent } from './reservation-values-upsert-dialog/reservation-values-upsert-dialog.component';
import { ValueItemType } from '@app/core/models/common.model';
import { newClientUid } from '@app/shared/utilities/client-uid.util';

interface ReservationValuesConfig {
  // Allow toggling acceptance inline (create mode dumps the whole catalog with accept toggles).
  acceptEditable: boolean;
  // Show a "+ New" button that adds items from the available (calculated-data) catalog, instead of
  // pre-filling the grid — used on update.
  addable?: boolean;
  valueItems: WritableSignal<ReservationValueDetailsModel[]>;
  // Provider for the available (calculated-data) items offered in the "+ New" dialog. Read on click,
  // so it reflects the current selection (e.g. the served car group) rather than a cached snapshot.
  availableItems?: () => ReservationValueDetailsModel[];
}

@Component({
  selector: 'app-reservation-values',
  standalone: true,
  imports: [SimpleGridComponent],
  templateUrl: './reservation-values.component.html',
  host: { class: 'd-flex flex-fill' }
})
export class ReservationValuesComponent {
  private _modalService = inject(NgbModal);

  config = input.required<ReservationValuesConfig>();

  // Emitted when acceptance is toggled, so the parent can flag the pre-paid warning / dirtiness.
  valueAcceptedChanged = output<{ code: string; isAccepted: boolean }>();

  private _grid = viewChild(SimpleGridComponent);
  private _items = computed(() => this.config().valueItems());

  acceptEditable = computed(() => this.config().acceptEditable);
  addable = computed(() => this.config().addable ?? false);

  baseActions = computed<Action[]>(() => {
    if (this.addable()) {
      // Update mode: add from the catalog, toggle acceptance via the edit dialog, and remove rows.
      return this.acceptEditable() ? ['create', 'update', 'delete'] : ['create', 'delete'];
    }
    return this.acceptEditable() ? ['update'] : ['details'];
  });

  rowActionSelector = () => [];

  refresh() {
    this._grid()?.refresh();
  }

  rowClassRules: RowClassRules<unknown> = {
    'hightlight-yellow': (params: unknown) => {
      const row = (params as RowClassParams<ReservationValueListModel>).data;
      return (row?.isAccepted ?? false) || (row?.isIncluded ?? false);
    }
  };

  listReservationValuesOperator = pipe(map(() => this._items()));

  createReservationValueOperator = pipe(
    switchMap(() => {
      // The same item may be added multiple times, so offer the full catalog (deduped by code just
      // for the picker — adding it again is done by reopening "+ New").
      const catalog = this.config().availableItems?.() ?? [];
      const selectable = [...new Map(catalog.map((x) => [x.code.toLowerCase(), x])).values()];

      const options: NgbModalOptions = { scrollable: true, centered: true, size: 'lg', fullscreen: 'md' };
      const modalRef = this._modalService.open(ReservationValuesUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({ mode: 'create', availableItems: selectable });

      return modalRef.closed.pipe(
        map((value: ReservationValueDetailsModel | undefined) => {
          if (!value) return;
          // Each add is a distinct row (unique client uid), so duplicate codes can coexist.
          this.config().valueItems.update((list) => [...list, { ...value, uid: value.uid || newClientUid() }]);
          this.valueAcceptedChanged.emit({ code: value.code, isAccepted: value.isAccepted });
        })
      );
    })
  );

  deleteReservationValueOperator = pipe(
    switchMap((event: DeleteEventDataModel) => {
      const removed = this._items().find((x) => x.uid === event.uid);
      this.config().valueItems.update((list) => list.filter((x) => x.uid !== event.uid));
      if (removed) {
        this.valueAcceptedChanged.emit({ code: removed.code, isAccepted: false });
      }
      return of(undefined);
    })
  );

  updateReservationValueOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const options: NgbModalOptions = { scrollable: true, centered: true, size: 'lg', fullscreen: 'md' };
      const modalRef = this._modalService.open(ReservationValuesUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({ mode: 'update', item: existing, acceptOnly: true });
      return modalRef.closed.pipe(
        map((value) => {
          this.config().valueItems.update((list) => list.map((x) => (x.uid === uid ? value : x)));
          this.valueAcceptedChanged.emit({ code: value.code, isAccepted: value.isAccepted });
        })
      );
    })
  );

  detailsReservationValueOperator = pipe(
    switchMap((uid: string) => {
      const existing = this._items().find((x) => x.uid === uid);
      if (!existing) return of(undefined);
      const options: NgbModalOptions = { scrollable: true, centered: true, size: 'lg', fullscreen: 'md' };
      const modalRef = this._modalService.open(ReservationValuesUpsertDialogComponent, options);
      modalRef.componentInstance.config.set({ mode: 'readonly', item: existing });
      return modalRef.closed;
    })
  );

  // The isAccepted checkbox is edited inline; sync the change into the store.
  acceptValueOperator = pipe(
    map((event: CellValueChangedEvent) => {
      if (event.colDef.field !== 'isAccepted') return undefined;

      const row = event.data as ReservationValueListModel;
      this.config().valueItems.update((list) =>
        list.map((x) => (x.uid === row.uid ? { ...x, isAccepted: row.isAccepted } : x))
      );
      this.valueAcceptedChanged.emit({ code: row.code, isAccepted: row.isAccepted });
      event.api.redrawRows({ rowNodes: [event.node] });
      return undefined;
    })
  );

  cellValueChangeOperator = computed(() => (this.acceptEditable() ? this.acceptValueOperator : null));

  columnDefs = computed<ColDef<ReservationValueDetailsModel>[]>(() => {
    // Read acceptEditable() here so this computed re-runs (and hands ag-grid a fresh columnDefs
    // reference) whenever it flips — e.g. readonly -> update. Without that, ag-grid keeps the
    // same column array and never refreshes the isAccepted checkboxes, so they stay disabled.
    const acceptEditable = this.acceptEditable();
    return [
      {
        field: 'itemType',
        valueFormatter: (params: ValueFormatterParams) => {
          return ValueItemType[params.value];
        },
        filter: false,
        floatingFilter: false,
        sort: 'asc',
        sortIndex: 1,
        sortable: false
      },
      {
        field: 'code',
        valueFormatter: (params: ValueFormatterParams) => {
          return params.value.toUpperCase();
        },
        filter: false,
        floatingFilter: false,
        sortable: false
      },
      {
        field: 'amountWithoutTax',
        valueFormatter: (params: ValueFormatterParams) => {
          return (Math.round((params.value + Number.EPSILON) * 100) / 100).toString();
        },
        filter: false,
        floatingFilter: false,
        sortable: false
      },
      {
        field: 'isSoldByBroker',
        headerName: 'Is Invoiced',
        filter: false,
        floatingFilter: false,
        sortable: false
      },
      {
        sort: 'desc',
        sortIndex: 0,
        field: 'isAccepted',
        filter: false,
        floatingFilter: false,
        sortable: false,
        editable: (params: EditableCallbackParams<ReservationValueDetailsModel, unknown>) =>
          acceptEditable && params.data?.code.toLowerCase() !== 'dayvalue'
      },
      {
        sort: 'desc',
        sortIndex: 2,
        field: 'isIncluded',
        filter: false,
        floatingFilter: false,
        sortable: false
      }
    ];
  });
}
